import React, { useState, useEffect, useRef } from 'react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
  disabled?: boolean;
}

const PullToRefresh: React.FC<PullToRefreshProps> = ({ onRefresh, children, disabled = false }) => {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const currentY = useRef(0);
  const elementRef = useRef<HTMLDivElement>(null);

  const PULL_THRESHOLD = 80; // Distance to trigger refresh
  const MAX_PULL = 120; // Maximum pull distance

  useEffect(() => {
    const element = elementRef.current;
    if (!element || disabled) return;

    let touchStartY = 0;
    let isDragging = false;

    const handleTouchStart = (e: TouchEvent) => {
      // Only allow pull-to-refresh when at the top of the scroll
      if (element.scrollTop === 0) {
        touchStartY = e.touches[0].clientY;
        isDragging = true;
        startY.current = touchStartY;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging || element.scrollTop > 0) {
        isDragging = false;
        setIsPulling(false);
        setPullDistance(0);
        return;
      }

      currentY.current = e.touches[0].clientY;
      const distance = currentY.current - touchStartY;

      if (distance > 0) {
        e.preventDefault(); // Prevent default scroll
        const pullAmount = Math.min(distance * 0.5, MAX_PULL); // Damping effect
        setPullDistance(pullAmount);
        setIsPulling(pullAmount > 10);

        if (pullAmount >= PULL_THRESHOLD) {
          // Visual feedback that refresh will trigger
        }
      }
    };

    const handleTouchEnd = async () => {
      if (!isDragging) return;

      isDragging = false;

      if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
        setIsRefreshing(true);
        try {
          await onRefresh();
        } catch (error) {
          console.error('Refresh error:', error);
        } finally {
          setIsRefreshing(false);
          setPullDistance(0);
          setIsPulling(false);
        }
      } else {
        // Spring back
        setPullDistance(0);
        setIsPulling(false);
      }
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: false });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd);

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onRefresh, pullDistance, isRefreshing, disabled]);

  const pullPercentage = Math.min((pullDistance / PULL_THRESHOLD) * 100, 100);
  const shouldTrigger = pullDistance >= PULL_THRESHOLD;

  return (
    <div
      ref={elementRef}
      style={{
        position: 'relative',
        height: '100%',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Pull to refresh indicator */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: `${Math.min(pullDistance, MAX_PULL)}px`,
          display: isPulling || isRefreshing ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          transform: `translateY(${-Math.min(pullDistance, MAX_PULL)}px)`,
          transition: isRefreshing ? 'none' : 'transform 0.2s ease-out',
          zIndex: 1000,
          boxShadow: isPulling ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
        }}
      >
        <div style={{ textAlign: 'center', color: 'white' }}>
          {isRefreshing ? (
            <>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔄</div>
              <div style={{ fontSize: '14px', fontWeight: '600' }}>Syncing...</div>
            </>
          ) : (
            <>
              <div
                style={{
                  fontSize: '24px',
                  marginBottom: '8px',
                  transform: `rotate(${pullPercentage * 3.6}deg)`,
                  transition: 'transform 0.1s ease-out',
                }}
              >
                {shouldTrigger ? '⬇️' : '⬇️'}
              </div>
              <div style={{ fontSize: '14px', fontWeight: '600' }}>
                {shouldTrigger ? 'Release to sync' : 'Pull to sync'}
              </div>
              <div
                style={{
                  width: '60px',
                  height: '4px',
                  background: 'rgba(255,255,255,0.3)',
                  borderRadius: '2px',
                  margin: '8px auto 0',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${pullPercentage}%`,
                    height: '100%',
                    background: 'white',
                    borderRadius: '2px',
                    transition: 'width 0.1s ease-out',
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ minHeight: '100%' }}>{children}</div>
    </div>
  );
};

export default PullToRefresh;

